import { BellTwoTone } from '@ant-design/icons';
import { Card, Modal, Space, Steps } from 'antd';
import axios from 'axios';
import { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import PPPageHeader from '../../../components/pageHeader/PPPageHeader';
import useStore from '../../../store/useStore';
import '../style.less';
import { AddOrderDeliveryProvider } from './AddOrderDeliveryContext';
import { deliveryDraftKey, readDraft, useClearDraftOnLeave } from './draft';
import Step1 from './Step1';
import Step2 from './Step2';
import Step3 from './Step3';

const routes = [
  { path: '/order', breadcrumbName: 'Хүргэлтүүд' },
  { path: 'first', breadcrumbName: 'Шинэ хүргэлтийн захиалга' },
];

const STEPS = [
  { title: 'Бараа', description: 'Барааны мэдээлэл' },
  { title: 'Хүргэлт', description: 'Хүргэлтийн мэдээлэл' },
  { title: 'Хянах', description: 'Мэдээлэл баталгаажуулах' },
];

const AddOrderDelivery = () => {
  const history = useHistory();
  const location = useLocation();

  const [orderBlock, setOrderBlock] = useState(false);
  const [orderBlockData, setOrderBlockData] = useState({});

  /**
   * Алхмыг URL-ийн `?step=1..3` агуулна — refresh болон browser-ийн
   * back/forward ажиллана.
   */
  const stepParam = Number(new URLSearchParams(location.search).get('step'));
  const urlStep =
    Number.isInteger(stepParam) && stepParam >= 1 ? Math.min(stepParam - 1, STEPS.length - 1) : 0;

  // Эхний рендер дээр л: draft-д дата байхгүй алхам руу шууд орохыг хориглоно
  // (жиш нь шинэ session дээр `?step=3` гэж гараар оровол 1-р алхмаас эхэлнэ)
  const isMounted = useRef(false);
  /**
   * Draft-ыг merchant-аар салгана — merchant солиход (нэг таб дотор reload
   * болдог тул) өмнөх merchant-ийн сонголт шинэ merchant дээр сэргэхгүй.
   */
  const merchantCode = useStore((state) => state.user.merchantCode);
  const draftKey = deliveryDraftKey(merchantCode);
  const draft = useRef(readDraft(draftKey)).current;
  // Хуудсаас гармагц бөглөсөн мэдээлэл цэвэрлэгдэнэ (refresh дээр сэргэсэн хэвээр)
  useClearDraftOnLeave(draftKey);
  /**
   * `formDataStep1.products` нь 1-р алхмыг БАТАЛСАН үед л бичигдэнэ (formValues
   * нь бөглөж байх зуур ч хадгалагддаг тул түүнээр шалгаж болохгүй).
   */
  const maxAllowedStep = draft.formDataStep2 ? 2 : draft.formDataStep1?.products ? 1 : 0;
  const current = isMounted.current ? urlStep : Math.min(urlStep, maxAllowedStep);

  /**
   * Алхам солихдоо шинэ history бичлэг үүсгэнэ — browser-ийн back дарахад
   * өмнөх алхам руу буцна. Зөвхөн зөвшөөрөгдөөгүй алхмыг залруулахдаа
   * `replace` хийнэ (буцах товч дахин тэр алхам руу оруулахгүй).
   */
  const goToStep = (step, { replace = false } = {}) => {
    const target = { pathname: location.pathname, search: `?step=${step + 1}` };
    return replace ? history.replace(target) : history.push(target);
  };

  const nextStep = () => goToStep(current + 1);
  const prevStep = () => goToStep(current - 1);

  useEffect(() => {
    isMounted.current = true;
    if (urlStep > maxAllowedStep) goToStep(maxAllowedStep, { replace: true });
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    axios
      .get('/api/v1/request/check-order-block')
      .then(({ data }) => {
        if (data.statusCode !== 200) {
          setOrderBlock(true);
          setOrderBlockData(data);
        }
      })
      .catch((e) => {
        console.error('check-order-block error: ', e);
      });
  }, []);

  const stepContent = [
    <Step1 nextStep={nextStep} prevStep={prevStep} />,
    <Step2 nextStep={nextStep} prevStep={prevStep} />,
    <Step3 prevStep={prevStep} />,
  ];

  return (
    <div>
      <PPPageHeader
        title="Шинэ хүргэлтийн захиалга"
        breadcrumb={{ routes }}
        onBack={() => history.push('/merchant/order/delivery')}
      />

      {orderBlock ? (
        <Modal
          title={
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <BellTwoTone style={{ fontSize: 14, color: '#08c' }} />
              Мэдэгдэл
            </span>
          }
          open={orderBlock}
          onCancel={() => setOrderBlock(false)}
          footer={null}
        >
          {orderBlockData?.errorMessageMn || orderBlockData?.message_mn}
        </Modal>
      ) : (
        /**
         * `key` нь draftKey — merchant солиход provider бүхэлдээ remount болж
         * төлөв нь тэглэгдэнэ. Эс бөгөөс дахин ачаалахаас өмнөх сүүлийн рендерт
         * ӨМНӨХ merchant-ийн бараа ШИНЭ merchant-ийн draft key дор бичигдээд
         * үлдэж байв.
         */
        <AddOrderDeliveryProvider key={draftKey} draftKey={draftKey}>
          <Space direction="vertical" size={16} style={{ width: '100%', padding: 16 }}>
            <Card size="middle">
              {/**
               * Гарчиг дээр дарж ӨМНӨХ алхам руу буцаж болно. Урагшаа алхам нь
               * disabled — мэдээллийг батлахгүйгээр цааш харайхыг хориглоно.
               */}
              <Steps
                current={current}
                responsive
                size="small"
                onChange={(step) => step < current && goToStep(step)}
                items={STEPS.map((step, index) => ({ ...step, disabled: index > current }))}
              />
            </Card>

            {stepContent[current]}
          </Space>
        </AddOrderDeliveryProvider>
      )}
    </div>
  );
};

export default AddOrderDelivery;
